/**
 * <ek-color-game> — A gentle color-matching game for toddlers and preschoolers.
 *
 * Shows a picture (emoji) and asks the child to tap the matching color.
 * The game talks (browser speech), plays soft chimes, bursts confetti on a
 * correct answer and throws a big party once every task in the game is done. A wrong tap gives a
 * soft "try again" with no penalty, timer, or score loss.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --pick  — Checks the tapped color (read from the button's data-color).
 *   --say   — Reads the current question out loud again.
 *   --sound — Turns talking and sounds on or off.
 *
 * Required child markup:
 *   [data-game-object]   — Element that shows the current picture.
 *   [data-game-feedback] — aria-live region for praise / retry text.
 *   [data-game-stars]    — Element that shows the stars earned.
 * Optional child markup:
 *   [data-game-party]        — Hidden celebration banner shown when the game is finished.
 *   [data-game-sound-on/off] — Icons toggled by the sound button.
 *   [data-color-label]       — Inside each color button, the spoken color name.
 *
 * Element dataset (on <ek-color-game>):
 *   data-rounds       — JSON array of { "e": emoji, "c": color key, "a": name }.
 *   data-correct      — Praise text shown on screen.
 *   data-retry        — Retry text shown on screen.
 *   data-say-prompt   — Spoken question; "[name]" is replaced by the picture name.
 *   data-say-correct  — Spoken praise; "[answer]" is replaced by the answer name.
 *   data-say-retry    — Spoken retry text.
 *   data-say-party    — Spoken celebration text.
 *   data-sound-on-label / data-sound-off-label — Accessible labels for the sound button.
 */
/** A game is every picture once, up to this many tasks; the reward comes at the end. */
const MAX_TASKS = 12;

/** Longest an animal recording plays, so the question comes quickly. */
const CLIP_MAX_MS = 4000;

/** One real-sound clip plays at a time across every game on the page. */
let activeClip = null;
function stopClip() {
  if (activeClip) {
    activeClip.pause();
    activeClip = null;
  }
}
document.addEventListener("ek-stop-sounds", stopClip);
/** The child's first name saved on this device by the games menu (members page), or "". */
export function kidName() {
  try {
    return (localStorage.getItem('edukids-kid-name') || '').trim();
  } catch {
    return '';
  }
}

const CONFETTI_COLORS = ['#EF4444', '#FACC15', '#3B82F6', '#22C55E', '#EC4899', '#F59E0B', '#4F46E5'];

/** Turns coded punctuation such as &#39; back into normal characters before display or speech. */
function plainText(text) {
  let value = String(text || '');
  for (let i = 0; i < 3 && /&(?:#\d+|#x[\da-f]+|[a-z]+);/i.test(value); i += 1) {
    value = new DOMParser().parseFromString(value, 'text/html').documentElement.textContent || '';
  }
  return value;
}

/** The chosen friendly voice, shared by every game so the voice never changes. */
const SHARED_VOICES = new Map();

class EkColorGame extends HTMLElement {
  #ac;
  #object;
  #feedback;
  #stars;
  #party;
  #soundButton;
  #rounds = [];
  #current = null;
  #lastIndex = -1;
  #lastPraise = '';
  #lastPrompt = '';
  #currentPrompt = '';
  #count = 0;
  #locked = false;
  #soundOn = true;
  #audio = null;
  #timers = new Set();
  #drag = null;
  #missesSinceParty = 0;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();

    this.#object = this.querySelector('[data-game-object]');
    this.#feedback = this.querySelector('[data-game-feedback]');
    this.#stars = this.querySelector('[data-game-stars]');
    this.#party = this.querySelector('[data-game-party]');
    this.#soundButton = this.querySelector('[command="--sound"]');

    try {
      this.#rounds = JSON.parse(this.dataset.rounds || '[]');
    } catch {
      this.#rounds = [];
    }
    if (!this.#object || this.#rounds.length === 0) return;

    const { signal } = this.#ac;
    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    this.#object.addEventListener('pointerdown', (event) => this.#dragStart(event), { signal });
    this.#object.addEventListener('pointermove', (event) => this.#dragMove(event), { signal });
    this.#object.addEventListener('pointerup', (event) => this.#dragEnd(event, false), { signal });
    this.#object.addEventListener('pointercancel', (event) => this.#dragEnd(event, true), { signal });
    // Many browsers load their voice list in the background, so ask early.
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.addEventListener?.('voiceschanged', () => window.speechSynthesis.getVoices(), { signal });
    }
    this.#syncSoundButton();
    this.#showStars();
    this.#next({ speak: false });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#timers.forEach((id) => clearTimeout(id));
    this.#timers.clear();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    stopClip();
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #later(callback, ms) {
    const id = setTimeout(() => {
      this.#timers.delete(id);
      callback();
    }, ms);
    this.#timers.add(id);
  }

  #animate(element, keyframes, options) {
    if (!element || this.#reducedMotion || typeof element.animate !== 'function') return null;
    return element.animate(keyframes, { easing: 'cubic-bezier(0.16, 1, 0.3, 1)', ...options });
  }

  #onCommand(event) {
    switch (event.command) {
      case '--pick':
        this.#pick(event.source);
        break;
      case '--say':
        this.#sayPrompt();
        break;
      case '--sound':
        this.#soundOn = !this.#soundOn;
        if (!this.#soundOn) {
          if ('speechSynthesis' in window) window.speechSynthesis.cancel();
          stopClip();
        }
        this.#syncSoundButton();
        break;
    }
  }

  /** Picks a praise phrase at random, never the same one twice in a row. */
  #nextPraise() {
    const options = (this.dataset.praise || '')
      .split('|')
      .map((phrase) => phrase.trim())
      .filter(Boolean);
    if (options.length === 0) return '';
    const fresh = options.length > 1 ? options.filter((phrase) => phrase !== this.#lastPraise) : options;
    const choice = fresh[Math.floor(Math.random() * fresh.length)];
    this.#lastPraise = choice;
    return choice;
  }

  #pick(button) {
    if (this.#locked || !this.#current) return;
    const color = button?.dataset?.color;
    if (!color) return;
    // Stop the animal recording so the voice never talks over it.
    stopClip();

    if (color === this.#current.c) {
      this.#locked = true;
      this.#count += 1;
      this.#showStars();
      const praise = this.#nextPraise();
      this.#setFeedback(praise ? `${praise} 🎉` : (this.dataset.correct || ''));

      const colorName = button.querySelector('[data-color-label]')?.textContent?.trim() || '';
      const isParty = this.#count >= this.#gameLength();
      const sent = this.#emit(isParty ? 'party' : 'correct', { perfect: this.#missesSinceParty === 0 });
      // The Prize Box (members page) fills in the reward it just gave, so the party can show it.
      this.#reward = isParty ? sent.reward || null : null;
      if (isParty) this.#missesSinceParty = 0;

      this.#chime();
      this.#confetti(isParty ? 60 : 24);
      this.#animate(this.#object, [
        { transform: 'scale(1) rotate(0deg)' },
        { transform: 'scale(1.25) rotate(-8deg)' },
        { transform: 'scale(1) rotate(0deg)' },
      ], { duration: 600 });
      this.#animate(button, [
        { transform: 'scale(1)' },
        { transform: 'scale(1.15)' },
        { transform: 'scale(1)' },
      ], { duration: 450 });

      if (isParty) {
        this.#showParty();
        this.#afterSpeech(this.#partyLine(), 3200, () => {
          this.#hideParty();
          // Start a fresh game: stars back to zero.
          this.#count = 0;
          this.#showStars();
          this.#locked = false;
          this.#next({ speak: true });
        });
      } else {
        const detail = (this.dataset.sayCorrect || '').replaceAll('[answer]', colorName);
        this.#afterSpeech([praise, detail].filter(Boolean).join(' '), 1700, () => {
          this.#locked = false;
          this.#next({ speak: true });
        });
      }
    } else {
      this.#setFeedback(this.dataset.retry || '');
      this.#missesSinceParty += 1;
      this.#emit('miss');
      this.#boop();
      if (this.#current.s) {
        // Animal sounds: say "try again", then play the same animal once more.
        const round = this.#current;
        this.#afterSpeech(this.dataset.sayRetry || '', 900, () => {
          if (this.#current === round && !this.#locked) this.#sayPrompt();
        });
      } else {
        this.#say(this.dataset.sayRetry || '');
      }
      this.#animate(button, [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-6px)' },
        { transform: 'translateX(6px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(0)' },
      ], { duration: 380 });
    }
  }

  /** Tells the prize box what happened (events bubble to the page). */
  #emit(kind, detail = {}) {
    const payload = { game: this.id, ...detail };
    this.dispatchEvent(new CustomEvent(`ek-game:${kind}`, { bubbles: true, detail: payload }));
    return payload;
  }

  #reward = null;

  /** How many tasks make one whole game: every picture once, up to MAX_TASKS. */
  #gameLength() {
    return Math.max(1, Math.min(this.#rounds.length, MAX_TASKS));
  }

  /** Shows progress like "3/10" so little ones see how close the big reward is. */
  #showStars() {
    if (this.#stars) this.#stars.textContent = `${this.#count}/${this.#gameLength()}`;
  }

  /** "Hooray! You got a cookie!" when there is a reward, otherwise a plain 5-star cheer. */
  #partyLine() {
    const name = this.#reward?.name || '';
    let line = '';
    if (!name || !this.dataset.sayReward) {
      line = plainText(this.dataset.sayParty || '');
    } else {
      line = plainText(this.dataset.sayReward).replaceAll('[name]', name);
      // English article: "an ice cream", "an orange long neck".
      if (/^[aeiou]/i.test(name)) line = line.replace(` a ${name}`, ` an ${name}`);
    }
    // "Great job, Mia!" first when the child's name is saved (members page).
    const child = kidName();
    const cheer = this.closest('[data-say-cheer]')?.dataset.sayCheer || '';
    if (child && cheer) line = `${plainText(cheer).replaceAll('[name]', child)} ${line}`;
    return line;
  }

  #setFeedback(rawText) {
    if (!this.#feedback) return;
    const text = plainText(rawText);
    // Clear first so screen readers re-announce repeated messages.
    this.#feedback.textContent = '';
    requestAnimationFrame(() => {
      this.#feedback.textContent = text;
    });
  }

  #deck = [];

  /**
   * Deals the pictures like a shuffled deck: every picture shows once before
   * any picture repeats, and a new deck never starts with the last picture.
   */
  #drawIndex() {
    if (this.#deck.length === 0) {
      const order = this.#rounds.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
      if (order.length > 1 && order[0] === this.#lastIndex) order.push(order.shift());
      this.#deck = order;
    }
    return this.#deck.shift();
  }

  /** Shows only a few big answer buttons per round: the right one plus random others. */
  #limitOptions() {
    const max = Number.parseInt(this.dataset.maxOptions || '0', 10);
    const buttons = [...this.querySelectorAll('[command="--pick"]')];
    if (!max || buttons.length <= max) {
      buttons.forEach((button) => { button.style.display = ''; });
      return;
    }
    const right = buttons.filter((button) => button.dataset.color === this.#current?.c);
    const others = buttons.filter((button) => !right.includes(button));
    for (let i = others.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [others[i], others[j]] = [others[j], others[i]];
    }
    const shown = new Set([...right.slice(0, 1), ...others.slice(0, max - Math.min(1, right.length))]);
    // Keep the buttons in a fresh random order so the answer isn't always in the same spot.
    const grid = buttons[0].parentElement;
    const order = [...shown].sort(() => Math.random() - 0.5);
    buttons.forEach((button) => { button.style.display = shown.has(button) ? '' : 'none'; });
    order.forEach((button) => grid?.append(button));
  }

  #next({ speak }) {
    const index = this.#drawIndex();
    this.#lastIndex = index;
    this.#current = this.#rounds[index];
    this.#currentPrompt = this.#nextPrompt();
    this.#limitOptions();

    this.#object.textContent = this.#current.e;
    this.#object.setAttribute('aria-label', this.#current.a || '');
    this.#setFeedback('');
    this.#animate(this.#object, [
      { transform: 'scale(.4)', opacity: 0 },
      { transform: 'scale(1.1)', opacity: 1 },
      { transform: 'scale(1)', opacity: 1 },
    ], { duration: 450 });

    if (speak) this.#sayPrompt();
  }

  /* ---------- Drag & drop ---------- */

  #dragStart(event) {
    if (this.#locked || !this.#current || this.#drag) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    this.#drag = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, over: null };
    this.#object.setPointerCapture?.(event.pointerId);
    this.#object.getAnimations?.().forEach((animation) => animation.cancel());
    this.#object.style.zIndex = '20';
    this.#object.style.cursor = 'grabbing';
  }

  #dragMove(event) {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) > 6) drag.moved = true;
    if (!drag.moved) return;

    this.#object.style.transform = `translate(${dx}px, ${dy}px) scale(1.1)`;

    const over = this.#buttonAt(event.clientX, event.clientY);
    if (over !== drag.over) {
      drag.over?.removeAttribute('data-drop-over');
      over?.setAttribute('data-drop-over', '');
      drag.over = over;
    }
  }

  #dragEnd(event, cancelled) {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.#drag = null;
    drag.over?.removeAttribute('data-drop-over');
    this.#object.releasePointerCapture?.(event.pointerId);
    this.#object.style.cursor = '';

    const target = cancelled ? null : drag.over;
    const from = this.#object.style.transform;
    this.#object.style.transform = '';
    this.#object.style.zIndex = '';

    if (!drag.moved) {
      // A simple tap on the picture repeats the question.
      this.#sayPrompt();
      return;
    }

    if (target) {
      this.#pick(target);
    } else if (from) {
      // Dropped outside a color: glide back home.
      this.#animate(this.#object, [{ transform: from }, { transform: 'translate(0, 0) scale(1)' }], { duration: 250 });
    }
  }

  #buttonAt(x, y) {
    for (const button of this.querySelectorAll('[command="--pick"]')) {
      const rect = button.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return button;
    }
    return null;
  }

  /* ---------- Talking ---------- */

  #sayPrompt() {
    if (!this.#current) return;
    const name = (this.#current.a || '').toLocaleLowerCase(document.documentElement.lang || undefined);
    const prompt = (this.#currentPrompt || this.#nextPrompt()).replaceAll('[name]', name);
    const ask = () => {
      if (this.#current.s) {
        // Real animal recording first, then the question.
        this.#playClip(this.#current.s, () => this.#say(prompt));
      } else {
        this.#say(prompt);
      }
    };
    // When the games menu just opened this game, greet the child by name first.
    const hello = this.#helloLine();
    if (hello) {
      this.#afterSpeech(hello, 600, () => {
        if (this.#current) ask();
      });
      return;
    }
    ask();
  }

  /** Lets other parts of the page (the awards) talk in the same friendly voice. */
  speak(text) {
    return this.#say(text);
  }

  /** "Hi Mia! Let's play!" once, right after the games menu opens this game. */
  #helloLine() {
    if (!this.dataset.greet) return '';
    delete this.dataset.greet;
    const child = kidName();
    const template = this.closest('[data-say-hello]')?.dataset.sayHello || '';
    if (!child || !template) return '';
    return plainText(template).replaceAll('[name]', child);
  }

  /** Plays a real sound clip (up to CLIP_MAX_MS), then runs `after`. */
  #playClip(src, after) {
    stopClip();
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    if (!this.#soundOn) {
      after?.();
      return;
    }
    const clip = new Audio(src);
    activeClip = clip;
    let finished = false;
    const finish = () => {
      // Skip if another game or the sound button stopped this clip.
      if (finished || activeClip !== clip) return;
      finished = true;
      stopClip();
      if (this.isConnected) this.#later(() => after?.(), 250);
    };
    clip.addEventListener('ended', finish);
    clip.addEventListener('error', finish);
    clip.play().catch(finish);
    this.#later(finish, CLIP_MAX_MS);
  }

  /** Picks a question wording at random, never the same one twice in a row. */
  #nextPrompt() {
    const options = (this.dataset.sayPrompt || '')
      .split('|')
      .map((phrase) => phrase.trim())
      .filter(Boolean);
    if (options.length === 0) return '';
    const fresh = options.length > 1 ? options.filter((phrase) => phrase !== this.#lastPrompt) : options;
    const choice = fresh[Math.floor(Math.random() * fresh.length)];
    this.#lastPrompt = choice;
    return choice;
  }

  #say(rawText) {
    const text = plainText(rawText);
    if (!this.#soundOn || !text || !('speechSynthesis' in window)) return;
    const synth = window.speechSynthesis;
    // Some browsers drop a new sentence spoken right after cancel(), so only
    // cancel when something is still talking, and wait a moment before speaking.
    const busy = synth.speaking || synth.pending;
    if (busy) synth.cancel();
    synth.resume?.();
    const utterance = new SpeechSynthesisUtterance(text);
    const lang = document.documentElement.lang || 'en';
    utterance.lang = lang;
    const voice = this.#friendlyVoice(lang);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    }
    // Slow, warm and bright, like a cheerful preschool teacher.
    utterance.rate = 0.85;
    utterance.pitch = 1.25;
    if (busy) {
      setTimeout(() => synth.speak(utterance), 80);
    } else {
      synth.speak(utterance);
    }
    return utterance;
  }

  /**
   * Speaks the text, then runs the callback once the voice has finished and
   * at least `minMs` has passed, so the next question never cuts a word off.
   * A safety timeout keeps the game moving if a device never reports the end.
   */
  #afterSpeech(text, minMs, callback) {
    const utterance = this.#say(text);
    let waitedEnough = false;
    let finishedSpeaking = !utterance;
    let done = false;

    const proceed = () => {
      if (done || !waitedEnough || !finishedSpeaking || !this.isConnected) return;
      done = true;
      // A short pause after the voice ends feels natural for little ones.
      this.#later(callback, 350);
    };

    if (utterance) {
      const markFinished = () => {
        finishedSpeaking = true;
        proceed();
      };
      utterance.addEventListener('end', markFinished);
      utterance.addEventListener('error', markFinished);
      this.#later(markFinished, 9000);
    }

    this.#later(() => {
      waitedEnough = true;
      proceed();
    }, minMs);
  }

  /**
   * Picks a friendly female voice for the page language when the device has one.
   * Voices come from the visitor's device, so names are matched by preference.
   */
  #friendlyVoice(lang) {
    // One voice for every game on the page, and for the whole visit.
    const voices = window.speechSynthesis.getVoices?.() || [];
    if (voices.length === 0) return null;
    const cacheKey = `edukids-voice-${lang.toLowerCase()}`;
    const cached = SHARED_VOICES.get(cacheKey);
    if (cached) return cached;
    let savedName = '';
    try { savedName = sessionStorage.getItem(cacheKey) || ''; } catch { /* storage blocked */ }
    const saved = savedName ? voices.find((voice) => voice.name === savedName) : null;
    if (saved) {
      SHARED_VOICES.set(cacheKey, saved);
      return saved;
    }
    const chosen = this.#rankVoice(voices, lang);
    if (chosen) {
      SHARED_VOICES.set(cacheKey, chosen);
      try { sessionStorage.setItem(cacheKey, chosen.name); } catch { /* storage blocked */ }
    }
    return chosen;
  }

  #rankVoice(voices, lang) {

    const base = lang.toLowerCase().split('-')[0];
    const sameLanguage = voices.filter((voice) => voice.lang?.toLowerCase().startsWith(base));
    if (sameLanguage.length === 0) return null;

    const preferred = [
      // English
      'samantha', 'ava', 'allison', 'susan', 'karen', 'serena', 'moira', 'tessa', 'fiona', 'victoria',
      'google us english', 'google uk english female', 'microsoft aria', 'microsoft jenny',
      'microsoft zira', 'microsoft ava', 'microsoft emma', 'microsoft michelle',
      // Portuguese
      'joana', 'catarina', 'luciana', 'fernanda', 'microsoft raquel', 'microsoft francisca', 'google português',
    ];
    const looksFemale = /female|woman|girl|mulher/i;

    const byName = (voice) => {
      const name = voice.name.toLowerCase();
      const index = preferred.findIndex((wanted) => name.includes(wanted));
      return index === -1 ? Infinity : index;
    };

    const ranked = [...sameLanguage].sort((a, b) => {
      // Prefer the visitor's regional accent (for example en-US) when the page sets one.
      const regionA = a.lang.toLowerCase() === lang.toLowerCase() ? 0 : 1;
      const regionB = b.lang.toLowerCase() === lang.toLowerCase() ? 0 : 1;
      return byName(a) - byName(b) || regionA - regionB;
    });

    return ranked.find((voice) => byName(voice) !== Infinity)
      || sameLanguage.find((voice) => looksFemale.test(voice.name))
      || null;
  }

  /* ---------- Sounds ---------- */

  #audioContext() {
    if (!this.#soundOn) return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!this.#audio) this.#audio = new AudioCtx();
    if (this.#audio.state === 'suspended') this.#audio.resume();
    return this.#audio;
  }

  #tone(ctx, frequency, start, duration, type = 'sine', volume = 0.18) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
    gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime + start);
    osc.stop(ctx.currentTime + start + duration + 0.05);
  }

  #chime() {
    const ctx = this.#audioContext();
    if (!ctx) return;
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => this.#tone(ctx, freq, i * 0.09, 0.35, 'triangle'));
  }

  #boop() {
    const ctx = this.#audioContext();
    if (!ctx) return;
    this.#tone(ctx, 330, 0, 0.18, 'sine', 0.12);
    this.#tone(ctx, 262, 0.14, 0.24, 'sine', 0.12);
  }

  #syncSoundButton() {
    if (!this.#soundButton) return;
    this.#soundButton.setAttribute('aria-pressed', String(this.#soundOn));
    const label = this.#soundOn ? this.dataset.soundOffLabel : this.dataset.soundOnLabel;
    if (label) this.#soundButton.setAttribute('aria-label', label);
    this.querySelector('[data-game-sound-on]')?.toggleAttribute('hidden', !this.#soundOn);
    this.querySelector('[data-game-sound-off]')?.toggleAttribute('hidden', this.#soundOn);
  }

  /* ---------- Celebrations ---------- */

  #confetti(pieces) {
    if (this.#reducedMotion) return;
    const layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:5;';
    this.appendChild(layer);

    const box = this.getBoundingClientRect();
    const objectBox = this.#object.getBoundingClientRect();
    const originX = objectBox.left - box.left + objectBox.width / 2;
    const originY = objectBox.top - box.top + objectBox.height / 2;

    for (let i = 0; i < pieces; i++) {
      const piece = document.createElement('span');
      const size = 6 + Math.random() * 7;
      const round = Math.random() > 0.5;
      piece.style.cssText = `position:absolute;left:${originX}px;top:${originY}px;width:${size}px;height:${round ? size : size * 0.5}px;border-radius:${round ? '9999px' : '2px'};background:${CONFETTI_COLORS[i % CONFETTI_COLORS.length]};`;
      layer.appendChild(piece);

      const angle = Math.random() * Math.PI * 2;
      const distance = 70 + Math.random() * (box.width / 2);
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance * 0.7 - 40;
      piece.animate([
        { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${Math.random() * 540}deg)`, opacity: 1, offset: 0.7 },
        { transform: `translate(calc(-50% + ${dx * 1.1}px), calc(-50% + ${dy + 80}px)) rotate(${Math.random() * 720}deg)`, opacity: 0 },
      ], { duration: 1100 + Math.random() * 500, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
    }

    this.#later(() => layer.remove(), 1800);
  }

  #showParty() {
    if (!this.#party) return;
    this.#party.toggleAttribute('data-open', true);
    this.#animate(this.#party, [
      { transform: 'scale(.6)', opacity: 0 },
      { transform: 'scale(1.05)', opacity: 1 },
      { transform: 'scale(1)', opacity: 1 },
    ], { duration: 350 });
    this.#danceBuddy();
  }

  #lastBuddy = '';
  #dance = null;

  /** Picks a different friendly character each party and makes it do a happy dance. */
  #danceBuddy() {
    const buddy = this.#party?.querySelector('[data-party-buddy]');
    if (!buddy) return;
    const partyText = this.#party.querySelector('[data-party-text]');
    if (partyText) {
      partyText.dataset.default ??= partyText.textContent;
      partyText.textContent = this.#reward ? this.#partyLine() : partyText.dataset.default;
    }
    if (this.#reward?.image) {
      // The award they just won, shown big, is what dances.
      const picture = document.createElement('img');
      picture.src = this.#reward.image;
      picture.alt = '';
      picture.width = 360;
      picture.height = 360;
      picture.style.cssText = 'display:block;width:min(64vw,300px);height:auto;aspect-ratio:1;border-radius:9999px;object-fit:cover;background:#FEF3C7;box-shadow:0 20px 40px rgba(0,0,0,.18);';
      buddy.replaceChildren(picture);
      buddy.removeAttribute('style');
    } else if (this.#reward?.icon) {
      // The reward they just won is the character that dances.
      buddy.textContent = this.#reward.icon;
      buddy.setAttribute('style', this.#reward.style || '');
    } else {
      buddy.removeAttribute('style');
    }
    const choices = (buddy.dataset.buddies || '').split(',').map((item) => item.trim()).filter((item) => item && item !== this.#lastBuddy);
    if (!this.#reward?.icon && choices.length > 0) {
      this.#lastBuddy = choices[Math.floor(Math.random() * choices.length)];
      buddy.textContent = this.#lastBuddy;
    }
    this.#dance?.cancel();
    if (typeof buddy.animate !== 'function') return;
    // A gentle wiggle-and-hop dance; smaller moves when the device asks for less motion.
    const s = this.#reducedMotion ? 0.35 : 1;
    this.#dance = buddy.animate([
      { transform: 'translateY(0) rotate(0deg) scale(1)' },
      { transform: `translateY(${-24 * s}px) rotate(${-14 * s}deg) scale(${1 + 0.08 * s})` },
      { transform: 'translateY(0) rotate(0deg) scale(1)' },
      { transform: `translateY(${-24 * s}px) rotate(${14 * s}deg) scale(${1 + 0.08 * s})` },
      { transform: 'translateY(0) rotate(0deg) scale(1)' },
    ], { duration: 900, iterations: Infinity, easing: 'ease-in-out' });
  }

  #hideParty() {
    this.#dance?.cancel();
    this.#dance = null;
    if (!this.#party) return;
    const exit = this.#animate(this.#party, [
      { transform: 'scale(1)', opacity: 1 },
      { transform: 'scale(.9)', opacity: 0 },
    ], { duration: 180 });
    if (exit) {
      exit.finished.then(() => { this.#party.toggleAttribute('data-open', false); }).catch(() => { this.#party.toggleAttribute('data-open', false); });
    } else {
      this.#party.toggleAttribute('data-open', false);
    }
  }
}

if (!customElements.get('ek-color-game')) {
  customElements.define('ek-color-game', EkColorGame);
}
